/**
 * A statistical chart as `show_chart` persists it and the chat renderer reads it back: the compiled
 * manifest under `charts/<call>/chart.json`, beside its exports. See CONTEXT.md "Statistical chart".
 */

export const CHART_KINDS = [
	"bar",
	"line",
	"scatter",
	"bubble",
	"pie",
	"histogram",
	"box",
	"violin",
	"heatmap",
	"contour",
	"histogram2d",
	"scatter3d",
	"surface",
	"radar",
	"parallel",
	"kde",
	"ecdf",
	"qq",
	"model",
] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

export const PROVENANCE_KINDS = ["teaching", "textbook", "user"] as const;
export type ProvenanceKind = (typeof PROVENANCE_KINDS)[number];

/** Where the data came from: constructed for teaching, read from a textbook (with its source and locator), or supplied by the person. */
export interface Provenance {
	kind: ProvenanceKind;
	description?: string;
	source?: string;
	locator?: string;
}

/** How a chart names where its data came from, in the chat and on its static page. */
export function provenanceLabel(p: Provenance): string {
	if (p.kind === "teaching") return "Teaching data: constructed for teaching, not observed.";
	if (p.kind === "textbook") return `Textbook data: ${p.source}, ${p.locator}. The calculations are checked in code; the observations are as the textbook gives them.`;
	return "User data: observations supplied by the person.";
}

export interface AxisLabel {
	label: string;
	unit?: string;
}

export interface ChartAxes {
	x?: AxisLabel;
	y?: AxisLabel;
	z?: AxisLabel;
}

/** Workspace-relative paths of a chart's persisted outputs; `json` is the manifest itself, `csv` the observations kept, `html` a static page. */
export interface ChartArtifacts {
	json: string;
	png: string;
	svg: string;
	csv: string;
	html: string;
}

/** What code calculated for one numeric input series; a statistic that does not apply is null or absent. */
export interface SeriesStatistics {
	name: string;
	/** The data role the summaries describe (values, y, or z for a matrix's cells); absent when no single role is summarized. */
	role?: string;
	n: number;
	mean?: number | null;
	sampleStandardDeviation?: number | null;
	q1?: number | null;
	median?: number | null;
	q3?: number | null;
	iqr?: number | null;
	lowerFence?: number | null;
	upperFence?: number | null;
	outliers?: number[];
	bins?: { edges: number[]; counts: number[]; rule: string };
	bins2d?: { xEdges: number[]; yEdges: number[]; counts: number[][]; rule: string };
	fit?: { slope: number; intercept: number };
	/** A kernel density's bandwidth and how it was chosen. */
	bandwidth?: { value: number; rule: string };
	/** Observations removed under `missing: "drop"`. */
	removed?: number;
}

/** A Plotly trace as the manifest stores it; `meta.role` tells a model's curve from its samples. */
export type PlotTrace = Record<string, unknown> & { type: string; meta?: { role?: string } & Record<string, unknown> };

export interface ChartManifest {
	version: 1;
	kind: ChartKind;
	title: string;
	description?: string;
	/** What was calculated and under which convention, in sentences for the reader. */
	explanation: string[];
	provenance: Provenance;
	axes?: ChartAxes;
	warnings: string[];
	plot: { data: PlotTrace[]; layout: Record<string, unknown>; config: Record<string, unknown> };
	statistics: SeriesStatistics[];
	model?: {
		distribution: string;
		parameters: Record<string, number>;
		sampleSize: number;
		seed: number;
		sample: number[];
		curve: { x: number[]; y: number[] };
	};
	artifacts: ChartArtifacts;
}

/** What `show_chart` puts in its result's `details`. */
export type ShowChartDetails =
	| { ok: true; chart: string; artifacts: ChartArtifacts }
	| { ok: false; code: ChartErrorCode; message: string };

export const CHART_ERROR_CODES = [
	"INVALID_DATA",
	"MISSING_VALUES",
	"LIMIT_EXCEEDED",
	"UNSUPPORTED_CHART",
	"UNSUPPORTED_MODEL",
	"INVALID_MODEL",
	"INVALID_PATH",
	"EXPORT_FAILED",
] as const;
export type ChartErrorCode = (typeof CHART_ERROR_CODES)[number];

/** Input series per chart, and observations across them; a matrix cell and a parallel-coordinates row each count as one. */
export const MAX_SERIES = 20;
export const MAX_OBSERVATIONS = 100_000;
